<template>
  <div class="settings-billing">
    <div class="settings-card-top"></div>

    <div class="settings-card-body">
      <br />
      <h3>Facturación</h3>
      <p class="settings-subtitle">Configura los datos fiscales a los que se facturarán tus compras de suscripción.</p>
      <div v-if="sectionError" class="alert alert-danger" role="alert">{{ sectionError }}</div>

      <div v-if="loading" class="billing-loading">Cargando información de facturación...</div>

      <template v-else>
        <div v-if="billingProfile" class="billing-profile-card">
          <div class="billing-profile-header">
            <div>
              <h6>{{ billingProfile.legal_name || '-' }}</h6>
              <div class="billing-rfc-pill">{{ billingProfile.rfc || '-' }}</div>
            </div>
            <button type="button" class="btn btn-primary btn-sm" @click="openBillingModal(true)">
              Editar datos fiscales
            </button>
          </div>

          <div class="billing-profile-highlights">
            <article class="billing-highlight-card">
              <span class="billing-label">Régimen fiscal</span>
              <p>{{ fiscalRegimeLabel(billingProfile.fiscal_regimen) }}</p>
            </article>
            <article class="billing-highlight-card">
              <span class="billing-label">Código postal</span>
              <p>{{ billingProfile.zipcode || '-' }}</p>
            </article>
            <article class="billing-highlight-card billing-highlight-card--full">
              <span class="billing-label">Uso de CFDI</span>
              <p>{{ cfdiUseLabel(billingProfile.cfdi_use) }}</p>
            </article>
          </div>
        </div>

        <div v-else class="billing-empty-card">
          <div class="billing-empty-content">
            <i class="fa fa-file-text-o"></i>
            <div>
              <h6>No has cargado tus datos fiscales</h6>
              <p>Tu factura se emitirá con público en general hasta que registres tu información de facturación.</p>
            </div>
          </div>
          <div class="billing-empty-action">
            <button type="button" class="btn btn-primary btn-sm" @click="openBillingModal(false)">
              Cargar datos fiscales
            </button>
          </div>
        </div>

        <section class="billing-invoices-card">
          <div class="billing-invoices-head">
            <h6>Historial de facturas</h6>
            <small>Comprobantes fiscales asociados a los cargos de suscripción.</small>
          </div>

          <div v-if="invoiceError" class="alert alert-danger mb-0" role="alert">{{ invoiceError }}</div>

          <div v-else-if="invoiceHistory.length > 0" class="table-responsive">
            <table class="table table-sm billing-invoices-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Concepto</th>
                  <th>Subtotal</th>
                  <th>IVA</th>
                  <th>Total</th>
                  <th>Estatus</th>
                  <th>Comprobante</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in invoiceHistory" :key="item.id">
                  <td>{{ formatDate(item.date) }}</td>
                  <td>{{ item.description }}</td>
                  <td>{{ formatCurrency(item.subtotal) }}</td>
                  <td>{{ formatCurrency(item.iva) }}</td>
                  <td>{{ formatCurrency(item.total) }}</td>
                  <td>
                    <span class="badge" :class="item.statusClass">{{ item.statusLabel }}</span>
                  </td>
                  <td>
                    <div class="billing-invoice-actions">
                      <button type="button" class="btn btn-sm btn-light" :disabled="!canDownloadInvoiceXml(item)" @click="downloadInvoiceXml(item)">XML</button>
                      <button type="button" class="btn btn-sm btn-light" :disabled="!canDownloadInvoicePdf(item)" @click="downloadInvoicePdf(item)">PDF</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-else class="billing-invoices-empty">Aún no hay facturas registradas para tu suscripción.</p>
        </section>
      </template>
    </div>

    <div v-if="showBillingModal" class="payment-modal-backdrop" @click.self="closeBillingModal">
      <div class="payment-modal" role="dialog" aria-modal="true" aria-labelledby="billing-modal-title">
        <div class="payment-modal-header">
          <h5 id="billing-modal-title">{{ editingBilling ? 'Actualizar datos de facturación' : 'Cargar datos de facturación' }}</h5>
          <button type="button" class="btn-close" aria-label="Cerrar" :disabled="saving" @click="closeBillingModal"></button>
        </div>

        <div class="payment-modal-body">
          <div v-if="billingFormError" class="alert alert-danger" role="alert">{{ billingFormError }}</div>

          <div class="billing-cif-upload-box">
            <div class="billing-cif-upload-head">
              <div>
                <h6>Cédula fiscal (PDF)</h6>
                <p>Opcional. Al seleccionar el PDF se leerán automáticamente algunos campos.</p>
              </div>
              <div class="billing-cif-actions">
                <label class="btn btn-light btn-sm mb-0">
                  {{ parsingCif ? 'Leyendo...' : 'Seleccionar PDF' }}
                  <input type="file" accept="application/pdf,.pdf" class="d-none" @change="onCifFileSelected" />
                </label>
              </div>
            </div>
            <small v-if="cifFile">Archivo seleccionado: {{ cifFile.name }}</small>
            <small v-if="cifError" class="text-danger d-block mt-2">{{ cifError }}</small>
          </div>

          <div class="settings-grid billing-grid">
            <div class="settings-field billing-span-1">
              <label>RFC *</label>
              <input type="text" class="form-control" v-model.trim="form.rfc" maxlength="13" @input="onUppercase('rfc', $event)" />
            </div>

            <div class="settings-field billing-span-2-half">
              <label>Nombre o razón social *</label>
              <input type="text" class="form-control" v-model.trim="form.legal_name" @input="onUppercase('legal_name', $event)" />
            </div>

            <div class="settings-field billing-span-1">
              <label>Código postal *</label>
              <input type="text" class="form-control" v-model.trim="form.zipcode" maxlength="5" inputmode="numeric" @input="onZipcodeInput" />
            </div>

            <div class="settings-field billing-span-2">
              <label>Régimen fiscal *</label>
              <select class="form-control" v-model="form.fiscal_regimen">
                <option value="">Selecciona un régimen fiscal</option>
                <option v-for="regimen in regimenCatalog" :key="regimen.code" :value="regimen.code">
                  {{ regimen.code }} - {{ regimen.name }}
                </option>
              </select>
            </div>

            <div class="settings-field billing-span-2">
              <label>Uso de CFDI *</label>
              <select class="form-control" v-model="form.cfdi_use">
                <option value="">Selecciona un uso de CFDI</option>
                <option v-for="cfdiUse in cfdiUseCatalog" :key="cfdiUse.code" :value="cfdiUse.code">
                  {{ cfdiUse.code }} - {{ cfdiUse.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="payment-modal-footer">
          <button type="button" class="btn btn-light" :disabled="saving" @click="closeBillingModal">Cancelar</button>
          <button type="button" class="btn btn-primary" :disabled="saving" @click="saveBilling">
            {{ saving ? 'Guardando...' : 'Guardar datos fiscales' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showPendingInvoiceConfirmModal" class="payment-modal-backdrop" @click.self="closePendingInvoiceConfirmModal">
      <div class="payment-modal" role="dialog" aria-modal="true" aria-labelledby="pending-invoice-confirm-title">
        <div class="payment-modal-header">
          <h5 id="pending-invoice-confirm-title">Facturar pagos pendientes</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Cerrar"
            :disabled="issuingPendingInvoices"
            @click="closePendingInvoiceConfirmModal"
          ></button>
        </div>

        <div class="payment-modal-body">
          <p v-if="pendingInvoiceConfirmCount === 1" class="mb-0">
            Tienes 1 pago pendiente por facturar. ¿Deseas emitir su factura ahora con estos datos fiscales?
          </p>
          <p v-else class="mb-0">
            Tienes {{ pendingInvoiceConfirmCount }} pagos pendientes por facturar. ¿Deseas emitir sus facturas ahora con estos datos fiscales?
          </p>
        </div>

        <div class="payment-modal-footer">
          <button type="button" class="btn btn-light" :disabled="issuingPendingInvoices" @click="closePendingInvoiceConfirmModal">No, después</button>
          <button type="button" class="btn btn-primary" :disabled="issuingPendingInvoices" @click="confirmIssuePendingInvoices">
            {{ issuingPendingInvoices ? 'Facturando...' : 'Sí, facturar ahora' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import accountService from '@/services/account';

export default {
  name: 'SettingsBillingSection',
  data() {
    return {
      loading: false,
      saving: false,
      parsingCif: false,
      showBillingModal: false,
      showPendingInvoiceConfirmModal: false,
      editingBilling: false,
      pendingInvoiceConfirmCount: 0,
      issuingPendingInvoices: false,
      billingProfile: null,
      subscriptionCharges: [],
      regimenCatalog: [],
      cfdiUseCatalog: [],
      sectionError: '',
      invoiceError: '',
      billingFormError: '',
      cifError: '',
      cifFile: null,
      form: this.emptyForm(),
    };
  },
  computed: {
    invoiceHistory() {
      const charges = Array.isArray(this.subscriptionCharges) ? this.subscriptionCharges : [];

      return charges
        .filter((item) => this.isInvoiceIssued(item))
        .slice(0, 30)
        .map((item, index) => {
        const total = Number(item.amount || 0);
        const subtotal = total > 0 ? Number((total / 1.16).toFixed(2)) : 0;
        const iva = Number((total - subtotal).toFixed(2));

        return {
          id: `inv-${item.id || index}`,
          chargeId: item.id,
          date: item.charged_at,
          description: item.description || 'Factura de suscripción',
          subtotal,
          iva,
          total,
          invoiceStatus: String(item.invoice_status || ''),
          invoiceUuid: item.invoice_uuid || null,
          invoiceXmlUrl: item.invoice_xml_url || null,
          statusLabel: this.mapInvoiceStatusLabel(item),
          statusClass: this.mapInvoiceStatusClass(item),
        };
      });
    },
    pendingInvoiceCharges() {
      const charges = Array.isArray(this.subscriptionCharges) ? this.subscriptionCharges : [];
      return charges
        .filter((item) => this.canIssueInvoiceForCharge(item))
        .slice(0, 10);
    },
  },
  async mounted() {
    await Promise.all([
      this.loadBilling(),
      this.loadInvoiceHistory(),
    ]);
      this.consumeAutoOpenBillingModalFlag();
  },
  methods: {
    emptyForm() {
      return {
        rfc: '',
        legal_name: '',
        fiscal_regimen: '',
        zipcode: '',
        cfdi_use: '',
      };
    },
    consumeAutoOpenBillingModalFlag() {
      const shouldOpen = String(this.$route?.query?.openBillingModal || '').trim() === '1';
      if (!shouldOpen) {
        return;
      }

      this.openBillingModal(Boolean(this.billingProfile));

      const query = { ...this.$route.query };
      delete query.openBillingModal;
      this.$router.replace({ query }).catch(() => {});
    },
    async loadBilling() {
      this.loading = true;
      this.sectionError = '';
      try {
        const response = await accountService.getBilling();
        if (response?.type === 'success') {
          this.billingProfile = response?.data?.billing_profile || null;
          this.regimenCatalog = response?.data?.catalogs?.regimens || [];
          this.cfdiUseCatalog = response?.data?.catalogs?.cfdi_uses || [];
        } else {
          this.billingProfile = null;
          this.regimenCatalog = [];
          this.cfdiUseCatalog = [];
        }
      } catch (error) {
        this.billingProfile = null;
        this.regimenCatalog = [];
        this.cfdiUseCatalog = [];
        this.sectionError = 'No se pudo cargar la información de facturación.';
      } finally {
        this.loading = false;
      }
    },
    async loadInvoiceHistory() {
      this.invoiceError = '';
      try {
        const response = await accountService.getSubscription();
        if (response?.type === 'success') {
          this.subscriptionCharges = Array.isArray(response?.data?.charges) ? response.data.charges : [];
          return;
        }

        this.subscriptionCharges = [];
      } catch (error) {
        this.subscriptionCharges = [];
        this.invoiceError = 'No se pudo cargar el historial de facturas.';
      }
    },
    mapInvoiceStatusLabel(item) {
      const status = String(item?.invoice_status || '').toLowerCase();
      if (status === 'success' || status === 'issued') return 'EMITIDA';
      if (status === 'failed') return 'ERROR';
      if (status === 'pending') return 'PENDIENTE';
      if (status === 'skipped') return 'OMITIDA';
      return this.billingProfile ? 'EMITIDA' : 'Público en general';
    },
    mapInvoiceStatusClass(item) {
      const status = String(item?.invoice_status || '').toLowerCase();
      if (status === 'success' || status === 'issued') return 'badge-light-success';
      if (status === 'failed') return 'badge-light-danger';
      if (status === 'pending') return 'badge-light-warning';
      if (status === 'skipped') return 'badge-light-secondary';
      return this.billingProfile ? 'badge-light-success' : 'badge-light-warning';
    },
    canDownloadInvoiceXml(item) {
      return Boolean(item?.chargeId && (item?.invoiceXmlUrl || item?.invoiceUuid));
    },
    isInvoiceIssued(item) {
      const status = String(item?.invoice_status || item?.invoiceStatus || '').toLowerCase();
      return status === 'success' || status === 'issued' || Boolean(item?.invoice_xml_url || item?.invoiceXmlUrl || item?.invoice_uuid || item?.invoiceUuid);
    },
    canIssueInvoiceForCharge(item) {
      if (!item?.id) {
        return false;
      }

       const status = String(item?.invoice_status || '').toLowerCase();
       if (status === 'skipped') {
         return false;
       }

      if (this.isInvoiceIssued(item)) {
        return false;
      }

      return Boolean(item?.charged_at);
    },
    async issuePendingInvoices() {
      const pending = this.pendingInvoiceCharges;
      if (pending.length <= 0) {
        return;
      }

      let successCount = 0;
      let failedCount = 0;

      for (const charge of pending) {
        try {
          const response = await accountService.issueSubscriptionInvoice(charge.id);
          if (response?.type === 'success') {
            successCount += 1;
          } else {
            failedCount += 1;
          }
        } catch (error) {
          failedCount += 1;
        }
      }

      await this.loadInvoiceHistory();

      if (successCount > 0 && failedCount === 0) {
        this.$toast.success(`Se facturaron ${successCount} pago(s) pendiente(s).`);
        return;
      }

      if (successCount > 0 && failedCount > 0) {
        this.$toast.warning(`Se facturaron ${successCount} pago(s), pero ${failedCount} no pudieron facturarse.`);
        return;
      }

      this.$toast.warning('No fue posible facturar los pagos pendientes en este momento.');
    },
    canDownloadInvoicePdf(item) {
      if (!item?.chargeId) {
        return false;
      }

      const status = String(item?.invoiceStatus || '').toLowerCase();
      return status === 'success' || status === 'issued' || Boolean(item?.invoiceUuid);
    },
    triggerBlobDownload(blob, fileName) {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    },
    async downloadInvoiceXml(item) {
      if (!this.canDownloadInvoiceXml(item)) {
        this.$toast.warning('Este cargo aún no tiene XML disponible.');
        return;
      }

      try {
        const response = await accountService.downloadSubscriptionInvoiceXml(item.chargeId);
        const blob = response instanceof Blob
          ? response
          : new Blob([response], { type: 'application/xml;charset=utf-8' });
        const suffix = item?.invoiceUuid || `charge-${item.chargeId}`;
        this.triggerBlobDownload(blob, `${suffix}.xml`);
      } catch (error) {
        const message = error?.response?.data?.message || 'No fue posible descargar el XML.';
        this.$toast.error(message);
      }
    },
    async downloadInvoicePdf(item) {
      if (!this.canDownloadInvoicePdf(item)) {
        this.$toast.warning('Este cargo aún no tiene PDF disponible.');
        return;
      }

      try {
        const response = await accountService.downloadSubscriptionInvoicePdf(item.chargeId);
        const blob = response instanceof Blob
          ? response
          : new Blob([response], { type: 'application/pdf' });
        const suffix = item?.invoiceUuid || `charge-${item.chargeId}`;
        this.triggerBlobDownload(blob, `${suffix}.pdf`);
      } catch (error) {
        const message = error?.response?.data?.message || 'No fue posible descargar el PDF.';
        this.$toast.error(message);
      }
    },
    openBillingModal(isEdit) {
      this.editingBilling = !!isEdit;
      this.billingFormError = '';
      this.cifError = '';
      this.cifFile = null;
      this.form = this.emptyForm();

      if (this.billingProfile) {
        this.form = {
          ...this.form,
          rfc: this.billingProfile.rfc || '',
          legal_name: this.billingProfile.legal_name || '',
          fiscal_regimen: this.billingProfile.fiscal_regimen || '',
          zipcode: this.billingProfile.zipcode || '',
          cfdi_use: this.billingProfile.cfdi_use || '',
        };
      }

      if (!this.form.cfdi_use && this.cfdiUseCatalog.some((item) => item.code === 'G03')) {
        this.form.cfdi_use = 'G03';
      }

      this.showBillingModal = true;
    },
    closeBillingModal() {
      if (this.saving) return;
      this.showBillingModal = false;
      this.billingFormError = '';
      this.cifError = '';
    },
    openPendingInvoiceConfirmModal(count) {
      this.pendingInvoiceConfirmCount = Number(count || 0);
      this.showPendingInvoiceConfirmModal = this.pendingInvoiceConfirmCount > 0;
    },
    closePendingInvoiceConfirmModal() {
      if (this.issuingPendingInvoices) {
        return;
      }

      this.showPendingInvoiceConfirmModal = false;
      this.pendingInvoiceConfirmCount = 0;
    },
    async confirmIssuePendingInvoices() {
      this.issuingPendingInvoices = true;
      try {
        await this.issuePendingInvoices();
      } finally {
        this.issuingPendingInvoices = false;
        this.closePendingInvoiceConfirmModal();
      }
    },
    onUppercase(field, event) {
      const value = event?.target?.value || '';
      this.form[field] = value.toUpperCase();
    },
    fiscalRegimeLabel(code) {
      if (!code) {
        return '-';
      }

      const match = this.regimenCatalog.find((item) => item.code === code);
      return match ? `${match.code} - ${match.name}` : code;
    },
    cfdiUseLabel(code) {
      if (!code) {
        return '-';
      }

      const match = this.cfdiUseCatalog.find((item) => item.code === code);
      return match ? `${match.code} - ${match.name}` : code;
    },
    onZipcodeInput(event) {
      const value = event?.target?.value || '';
      this.form.zipcode = value.replace(/\D/g, '').slice(0, 5);
    },
    async onCifFileSelected(event) {
      const file = event?.target?.files?.[0] || null;
      this.cifFile = file;
      this.cifError = '';

      if (!file) {
        return;
      }

      await this.parseCifFile();
    },
    async parseCifFile() {
      if (!this.cifFile) return;

      this.cifError = '';
      const formData = new FormData();
      formData.append('cif_file', this.cifFile);

      this.parsingCif = true;
      try {
        const response = await accountService.parseBillingCif(formData);
        if (response?.type !== 'success') {
          this.cifError = response?.message || 'No se pudo leer la cédula fiscal.';
          return;
        }

        const parsed = response?.data?.parsed_data || {};
        this.form = {
          ...this.form,
          rfc: parsed.rfc || this.form.rfc,
          legal_name: parsed.legal_name || this.form.legal_name,
          fiscal_regimen: parsed.fiscal_regimen && this.regimenCatalog.some((item) => item.code === parsed.fiscal_regimen)
            ? parsed.fiscal_regimen
            : this.form.fiscal_regimen,
          zipcode: parsed.zipcode || this.form.zipcode,
          cfdi_use: parsed.cfdi_use && this.cfdiUseCatalog.some((item) => item.code === parsed.cfdi_use)
            ? parsed.cfdi_use
            : this.form.cfdi_use,
        };

        this.$toast.success('Se leyó la cédula fiscal y se precargaron los datos detectados.');
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        this.cifError = apiMessage || 'No se pudo leer la cédula fiscal.';
      } finally {
        this.parsingCif = false;
      }
    },
    validateRequiredFields() {
      this.billingFormError = '';
      if (!this.form.rfc || !this.form.legal_name || !this.form.fiscal_regimen || !this.form.zipcode || !this.form.cfdi_use) {
        this.billingFormError = 'RFC, razón social, régimen fiscal, código postal y uso de CFDI son obligatorios.';
        return false;
      }

      return true;
    },
    async saveBilling() {
      if (!this.validateRequiredFields()) {
        return;
      }

      this.billingFormError = '';

      const payload = new FormData();
      payload.append('rfc', this.form.rfc);
      payload.append('legal_name', this.form.legal_name);
      payload.append('fiscal_regimen', this.form.fiscal_regimen);
      payload.append('zipcode', this.form.zipcode);
      payload.append('cfdi_use', this.form.cfdi_use);

      this.saving = true;
      try {
        const response = await accountService.saveBilling(payload);
        if (response?.type !== 'success') {
          this.billingFormError = response?.message || 'No se pudo guardar la información de facturación.';
          return;
        }

        this.billingProfile = response?.data?.billing_profile || null;
        this.$toast.success(response?.message || 'Datos de facturación guardados correctamente.');
        this.showBillingModal = false;

        await this.loadInvoiceHistory();

        const pendingCount = this.pendingInvoiceCharges.length;
        if (pendingCount > 0) {
          this.openPendingInvoiceConfirmModal(pendingCount);
        }
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        this.billingFormError = apiMessage || 'No se pudo guardar la información de facturación.';
      } finally {
        this.saving = false;
      }
    },
    formatDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(amount);
    },
  },
};
</script>
